import { useCurrentLanguage } from '@app/i18n';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DateFormats, FormattableDateValue } from '../../types';
import isDateValid from '../../utils/isDateValid';
import useFormatDate from '../useFormatDate';

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const MONTHISH = DAY * 30;
const YEAR = DAY * 365;

/**
 *
 */
const useFormatRelativeDate = () => {
  const language = useCurrentLanguage();
  const { t } = useTranslation();
  const formatDate = useFormatDate();

  return useCallback(
    (
      value: FormattableDateValue,
      format: DateFormats = 'date_and_time',
      forceRelativeFormat: boolean = false,
    ): string => {
      if (!value || !isDateValid(value)) {
        return t('N/A');
      }

      const now = new Date();
      const date = new Date(value);

      const diff = date.getTime() - now.getTime();

      //
      // Only use relative dates for roughly 3 months
      // and relative format is not explicitly set
      if (!forceRelativeFormat && Math.abs(diff) > MONTHISH * 3) {
        return formatDate(date, format);
      }

      let amount = 0;

      const formatter = new Intl.RelativeTimeFormat(language.locale, {
        numeric: 'auto',
      });

      //
      // We only hit this condition if forceRelativeFormat is explicitly set
      if (Math.abs(diff) >= YEAR) {
        amount = Math.round(diff / YEAR);

        //
        // Renders "1 year ago" vs. "last year"
        const formatter = new Intl.RelativeTimeFormat(language.locale, {
          numeric: 'always',
        });

        return formatter.format(amount, 'years');
      }

      if (Math.abs(diff) < MINUTE) {
        amount = Math.round(diff / SECOND);
        return formatter.format(amount, 'seconds');
      }

      if (Math.abs(diff) < HOUR) {
        amount = Math.round(diff / MINUTE);
        return formatter.format(amount, 'minutes');
      }

      if (Math.abs(diff) < DAY) {
        amount = Math.round(diff / HOUR);
        return formatter.format(amount, 'hours');
      }

      if (Math.abs(diff) < MONTHISH) {
        amount = Math.round(diff / DAY);
        return formatter.format(amount, 'days');
      }

      if (Math.abs(diff) < MONTHISH * 6) {
        amount = Math.round(diff / MONTHISH);
        return formatter.format(amount, 'months');
      }

      return formatDate(date, format);
    },
    [formatDate, language.locale, t],
  );
};

export default useFormatRelativeDate;
